import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RefundService } from '../../features/refund/services/refund.service';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { ModuleIds } from '../../shared/permission-constants/permission-const';
import { PermissionService } from '../../shared/services/permission.service';

@Component({
  selector: 'app-refund-details',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatTabsModule,
    MatSelectModule,
    MatIconModule,
    MatMenuModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './refund-details.component.html',
  styleUrl: './refund-details.component.scss',
})
export class RefundDetailsComponent {
  search: any;
  allRefunds: any;
  refundId: string | null;
  selectedTabIndex: any = 0;
  readonly dialog = inject(MatDialog);
  isDropdownOpen = false;
  selectedRefund: any;
  refundDetails: any;
  canCreateRefund$ = this.permissionService.hasPermission(
    ModuleIds.Refund,
    'Create'
  );
  canApproveRejectRefund$ = this.permissionService.hasPermission(
    ModuleIds.Refund,
    'Approve/Reject'
  );

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private refundService: RefundService,
    private permissionService: PermissionService
  ) {}

  ngOnInit() {
    this.fetchAllRefunds();
    this.route.queryParamMap.subscribe((params) => {
      this.refundId = params.get('refundId');
      if (this.refundId) {
        this.getRefundById(Number(this.refundId));
      }
    });
  }

  selectTab(index: number) {
    this.selectedTabIndex = index;
    this.selectedRefund = this.allRefunds[index];
    if (this.selectedRefund) {
      this.router.navigate(['/refund/refund-details'], {
        queryParams: { refundId: this.selectedRefund?.id },
      });
    }
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  getRefundById(id: any) {
    this.refundService.fetchRefundById(Number(id)).subscribe({
      next: (data: any) => {
        this.refundDetails = data;
      },
      error: (error) => {
        this.toastr.error(error?.message);
      },
    });
  }

  toggleDropdown(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  public fetchAllRefunds() {
    const input = {
      search: this.search,
    };
    this.refundService.getAllRefunds(input).subscribe({
      next: (data: any) => {
        this.allRefunds = data?.refunds;
        if (this.refundId) {
          const targetId = +this.refundId;
          this.selectedTabIndex = this.allRefunds.findIndex((c: any) => {
            return c.id === targetId;
          });
          if (this.selectedTabIndex === -1) {
            this.selectedTabIndex = 0;
          }
        } else {
          this.selectedTabIndex = 0;
        }
        this.selectedRefund = this.allRefunds[this.selectedTabIndex] || {};
      },
      error: (error) => {
        this.toastr.error(error?.message);
      },
    });
  }

  onEdit() {
    this.router.navigate([`/refund/refund-edit/${this.refundId}`]);
  }

  handleApproval(isApproved: boolean): void {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: isApproved ? 'Confirm Approval' : 'Confirm Rejection',
        message: isApproved
          ? 'Are you sure you want to approve this refund?'
          : 'Are you sure you want to reject this refund?',
        confirmText: 'Confirm',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.refundService
          .approveOrRejectRefund(Number(this.refundId), isApproved)
          .subscribe({
            next: (res) => {
              console.log(res);
              this.toastr.success('Refund status updated.');
              this.getRefundById(this.refundId);
              this.fetchAllRefunds();
              if (isApproved) {
                this.generatePdf(this.refundId);
              }
            },
            error: (err) => {
              console.error('Error updating refund status:', err);
            },
          });
      }
    });
  }

  onViewDocument() {
    const url = this.refundDetails?.refundUrl;
    const token = localStorage.getItem('AUTH_TOKEN');
    fetch(url, {
      method: 'GET',
      headers: {
        authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then((response: any) => response.blob())
      .then((blob: any) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      })
      .catch((error: any) => {
        console.error('Error:', error);
      });
  }

  generatePdf(refundId: any) {
    this.refundService.generateRefundPdf(Number(refundId)).subscribe({
      next: (res) => {
        this.fetchAllRefunds();
        this.getRefundById(this.refundId);
        this.toastr.success('Refund pdf successfully generated');
      },
      error: (err) => {
        console.log('Error in generate pdf', err);
      },
    });
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    this.search = input.value;
    this.fetchAllRefunds();
  }
}
